import { ChevronDownIcon } from "@chakra-ui/icons";
import type {
  As,
  ButtonProps,
  HTMLChakraProps,
  PopoverProps,
  StackProps,
  SystemStyleObject,
  ThemingProps
} from "@chakra-ui/react";
import {
  Box,
  Button,
  ButtonGroup,
  Collapse,
  IconButton,
  Stack,
  useDisclosure,
  useOutsideClick,
  useStyleConfig,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import React, { useRef } from "react";

export interface SplitButtonProps<T extends As> extends ThemingProps<"Button"> {
  menuItems?: ReactNode[]
  buttonProps: HTMLChakraProps<T> & ButtonProps & { collapseOnClick?: boolean };
  stackProps?: StackProps;
  splitterColor?: ButtonProps["borderColor"];
  popoverProps?: PopoverProps;
  popoverStyle?: SystemStyleObject;
}

export function SplitButton<T extends As>({
  buttonProps,
  splitterColor,
  menuItems,
  popoverProps,
  popoverStyle,
  stackProps,
  ...rest
}: SplitButtonProps<T>) {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const buttonStyle = useStyleConfig("Button", {
    variant: buttonProps.variant,
  });
  const mainButtonRef = useRef<HTMLButtonElement>(null);
  const collapseButtonRef = useRef<HTMLButtonElement>(null);

  useOutsideClick({
    ref: mainButtonRef,
    handler: (e) => {
      if (collapseButtonRef.current?.contains(e.target as Node)) return;
      onClose();
    },
  });

  return (
    <ButtonGroup
      isAttached
      {...rest}
      className="split-button"
      sx={{ position: "relative" }}
    >
      <Button
        ref={mainButtonRef}
        {...buttonProps}
        sx={{
          whiteSpace: "nowrap",
          borderRightRadius: menuItems ? 0 : buttonStyle.borderRadius,
          borderBottomLeftRadius:
            isOpen && menuItems ? 0 : buttonStyle.borderRadius,
          flex: "auto",
          justifyContent: menuItems ? "start" : 'center',
        }}
        onClick={(e) => {
          if (buttonProps.collapseOnClick) {
            onToggle()
          }
          else
            buttonProps.onClick?.(e);
        }}
      />

      {menuItems && (
        <Button
          ref={collapseButtonRef}
          as={IconButton}
          icon={
            <ChevronDownIcon
              boxSize={4}
              sx={{
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          }
          borderLeftRadius={0}
          px={2}
          sx={{
            borderBottomRightRadius: isOpen ? 0 : buttonStyle.borderRadius,
            _focus: { boxShadow: "none" },
          }}
          onClick={onToggle}
        ></Button>
      )}

      {menuItems && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            width: "100%",
            zIndex: "tooltip",
            boxShadow: "md",
            visibility: isOpen ? "visible" : "hidden",
          }}
          onClick={onClose}
        >
          <Collapse in={isOpen} animateOpacity>
            <Stack
              gap={0}
              {...stackProps}
              sx={{
                bg: buttonStyle.bg,
                borderTopRadius: 0,
                borderBottomRadius: buttonStyle.borderRadius,
                overflow: "hidden",
                "> *": {
                  borderTop: "1px solid",
                  borderColor: splitterColor,
                  borderRadius: "0 !important",
                },
                ...stackProps?.sx,
              }}
            >
              {menuItems.map((item, index) => {
                return (<React.Fragment key={index}>{item}</React.Fragment>)
              })}
            </Stack>
          </Collapse>
        </Box>
      )}
    </ButtonGroup>
  );
}
